import { faSync, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import type { DropdownChoice } from '@companion-app/shared/Model/Common.js'
import { StaticAlert } from '~/Components/Alert.js'
import { Button } from '~/Components/Button'
import { SimpleDropdownInputField } from '~/Components/DropdownInputFieldSimple.js'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import type { UserConfigProps } from '../Components/Common.js'
import { UserConfigHeadingRow } from '../Components/UserConfigHeadingRow.js'
import { UserConfigNumberInputRow } from '../Components/UserConfigNumberInputRow.js'
import { UserConfigPortNumberRow } from '../Components/UserConfigPortNumberRow.js'
import { UserConfigSwitchRow } from '../Components/UserConfigSwitchRow.js'
import { UserConfigTextInputRow } from '../Components/UserConfigTextInputRow.js'

export const HttpsConfig = observer(function HttpsConfig(props: UserConfigProps) {
	const createSslCertificateMutation = useMutationExt(trpc.userConfig.sslCertificateCreate.mutationOptions())
	const deleteSslCertificateMutation = useMutationExt(trpc.userConfig.sslCertificateDelete.mutationOptions())
	const renewSslCertificateMutation = useMutationExt(trpc.userConfig.sslCertificateRenew.mutationOptions())

	const createSslCertificate = useCallback(() => {
		console.log('create SSL certificate')
		createSslCertificateMutation.mutateAsync().catch((err) => {
			console.error('Failed to create SSL certificate:', err)
		})
	}, [createSslCertificateMutation])

	const deleteSslCertificate = useCallback(() => {
		console.log('delete SSL certificate')
		deleteSslCertificateMutation.mutateAsync().catch((err) => {
			console.error('Failed to delete SSL certificate:', err)
		})
	}, [deleteSslCertificateMutation])

	const renewSslCertificate = useCallback(() => {
		console.log('renew SSL certificate')
		renewSslCertificateMutation.mutateAsync().catch((err) => {
			console.error('Failed to renew SSL certificate:', err)
		})
	}, [renewSslCertificateMutation])

	return (
		<>
			<UserConfigHeadingRow label="HTTPS Web Server" helpAction="/user-guide/config/settings#https-web-server" />

			<tr>
				<td colSpan={3}>
					<p>An HTTPS server can be enabled for the Companion web interfaces should your deployment require it.</p>
					<StaticAlert color="danger">
						Never expose the Companion web interface directly to the Internet. Note that HTTPS alone does not provide
						additional security for this configuration.
					</StaticAlert>
				</td>
			</tr>

			<UserConfigSwitchRow userConfig={props} label="HTTPS Web Server" field="https_enabled" />

			{props.config.https_enabled && (
				<>
					<UserConfigPortNumberRow userConfig={props} label="HTTPS Port" field="https_port" />

					<tr>
						<td>Certificate Type</td>
						<td>
							<SimpleDropdownInputField
								id={undefined}
								value={props.config.https_cert_type}
								setValue={(val) => props.setValue('https_cert_type', val)}
								choices={certTypeOptions}
							/>
						</td>
						<td>
							<Button onClick={() => props.resetValue('https_cert_type')} title="Reset to default">
								<FontAwesomeIcon icon={faUndo} />
							</Button>
						</td>
					</tr>

					{props.config.https_cert_type === 'self' && (
						<tr>
							<td colSpan={3}>
								<table className="table table-responsive-sm">
									<tbody>
										<tr>
											<td colSpan={3}>This tool will help create a self-signed certificate for the server to use.</td>
										</tr>

										<UserConfigTextInputRow
											userConfig={props}
											label="Common Name (Domain Name)"
											field="https_self_cn"
										/>
										<UserConfigNumberInputRow
											userConfig={props}
											label="Certificate Expiry Days"
											field="https_self_expiry"
											min={1}
											max={65535}
										/>

										<tr>
											<td>
												Certificate Details
												<br />
												{props.config.https_self_cert && props.config.https_self_cert.length > 0 ? (
													<ul>
														<li>Common Name: {props.config.https_self_cert_cn}</li>
														<li>Created: {props.config.https_self_cert_created}</li>
														<li>Expiry Period: {props.config.https_self_cert_expiry}</li>
													</ul>
												) : (
													<ul>
														<li>No certificate available</li>
													</ul>
												)}
											</td>
											<td>
												{props.config.https_self_cert && props.config.https_self_cert.length > 0 ? (
													<div className="my-3">
														<Button onClick={renewSslCertificate} color="success" className="mb-2">
															<FontAwesomeIcon icon={faSync} />
															&nbsp;Renew
														</Button>
														<br />
														<Button onClick={deleteSslCertificate} color="danger">
															<FontAwesomeIcon icon={faTrash} />
															&nbsp;Delete
														</Button>
													</div>
												) : (
													<Button onClick={createSslCertificate} color="success">
														<FontAwesomeIcon icon={faSync} />
														&nbsp;Generate
													</Button>
												)}
											</td>
											<td>&nbsp;</td>
										</tr>
									</tbody>
								</table>
							</td>
						</tr>
					)}

					{props.config.https_cert_type === 'external' && (
						<tr>
							<td colSpan={3}>
								<table className="table table-responsive-sm">
									<tbody>
										<tr>
											<td colSpan={3}>
												<p>
													This requires you to generate your own self-signed certificate or go through a certificate
													authority. A properly signed certificate will work.
												</p>
												<StaticAlert color="danger">
													This option is provided as-is. Support will not be provided for this feature. <br />
													DO NOT POST GITHUB ISSUES IF THIS DOES NOT WORK.
												</StaticAlert>
											</td>
										</tr>

										<UserConfigTextInputRow
											userConfig={props}
											label="Private Key File (full path)"
											field="https_ext_private_key"
										/>
										<UserConfigTextInputRow
											userConfig={props}
											label="Certificate File (full path)"
											field="https_ext_certificate"
										/>
										<UserConfigTextInputRow
											userConfig={props}
											label={
												<>
													Chain File (full path)
													<br />
													*Optional
												</>
											}
											field="https_ext_chain"
										/>
									</tbody>
								</table>
							</td>
						</tr>
					)}
				</>
			)}
		</>
	)
})

const certTypeOptions: DropdownChoice[] = [
	{ id: 'self', label: 'Self Signed' },
	{ id: 'external', label: 'External' },
]
